import { doc, onSnapshot } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { db } from "../firebase";
import "../styles/Services.css";
// eslint-disable-next-line no-unused-vars
import { useScrollReveal } from "../hooks/useScrollReveal";

const defaultServices = [
  {
    id: 1,
    title: "Mobile Game Development",
    description: "Full-cycle mobile game development for iOS and Android platforms with monetization strategy and engaging gameplay mechanics.",
    icon: "🎮",
    features: ["Unity & Unreal Engine", "Cross-Platform Deployment", "In-App Purchases", "Multiplayer Integration", "Game Balance & Tuning"],
    popular: true
  },
  {
    id: 2,
    title: "App Development",
    description: "Custom mobile application development with focus on user experience, performance optimization, and scalable architecture.",
    icon: "📱",
    features: ["Native & Hybrid Apps", "UI/UX Design", "API Integration", "Cloud Backend", "Real-time Sync"],
    popular: true
  },
  {
    id: 3,
    title: "LiveOps & Analytics",
    description: "Data-driven live operations to maximize engagement, retention, and revenue through continuous optimization.",
    icon: "📊",
    features: ["Real-time Analytics", "A/B Testing", "User Segmentation", "Event Management", "Retention Campaigns"],
    popular: false
  },
  {
    id: 4,
    title: "AR/VR Solutions",
    description: "Immersive augmented and virtual reality experiences for next-gen applications and enterprise training solutions.",
    icon: "🥽",
    features: ["ARKit & ARCore", "3D Modeling", "Interactive Experiences", "Enterprise Solutions", "Spatial Computing"],
    popular: false
  },
  {
    id: 5,
    title: "Blockchain Gaming",
    description: "Web3 integration including NFTs, tokens, and decentralized gaming ecosystems with secure smart contracts.",
    icon: "⛓️",
    features: ["Smart Contracts", "NFT Integration", "Crypto Wallets", "Play-to-Earn Models", "Marketplace Development"],
    popular: false
  },
  {
    id: 6,
    title: "Consulting & Strategy",
    description: "Expert guidance on product strategy, market positioning, technical architecture, and growth planning.",
    icon: "💡",
    features: ["Market Analysis", "Product Roadmap", "Technical Audit", "Growth Strategy", "Competitive Research"],
    popular: false
  }
];

const industries = [
  { name: "Gaming", icon: "🎯", description: "Casual, Hyper-casual, Mid-core games" },
  { name: "Health & Fitness", icon: "❤️", description: "Wellness, tracking, telemedicine apps" },
  { name: "E-commerce", icon: "🛒", description: "Shopping, marketplace, payment solutions" },
  { name: "Education", icon: "📚", description: "E-learning, LMS, educational games" },
  { name: "Finance", icon: "💰", description: "Fintech, banking, investment platforms" },
  { name: "Social", icon: "👥", description: "Community, networking, messaging apps" }
];

const Services = () => {
  const [services, setServices] = useState(defaultServices);
  const [hoveredCard, setHoveredCard] = useState(null);
  useScrollReveal();

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "home", "services"),
      (snapshot) => {
        if (snapshot.exists() && snapshot.data().services) {
          setServices(snapshot.data().services);
        } else {
          setServices(defaultServices);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <div className="services-page">
      <SEO title="Services" description="Mobile game development, app development, LiveOps, AR/VR, blockchain gaming, and consulting. End-to-end digital solutions by MacroMatrix." path="/services" />
      <section className="services-hero">
        <div className="hero-bg-pattern" />
        <div className="hero-content">
          <span className="hero-badge">What We Offer</span>
          <h1>Our Services</h1>
          <p>Comprehensive solutions for your digital transformation journey - from concept to launch and beyond</p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">150+</span>
              <span className="stat-label">Projects Delivered</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">50M+</span>
              <span className="stat-label">Downloads</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">98%</span>
              <span className="stat-label">Client Satisfaction</span>
            </div>
          </div>
        </div>
      </section>

      <section className="services-grid-section reveal">
        <div className="services-intro">
          <h2>End-to-End Development Excellence</h2>
          <p>From concept to launch and beyond, we provide full-stack services that cover every aspect of your project lifecycle. Our team combines technical expertise with creative vision to deliver exceptional results.</p>
        </div>
        
        <div className="services-grid">
          {services.map((service, index) => (
            <div 
              className={`service-card ${hoveredCard === service.id ? 'hovered' : ''} ${service.popular ? 'popular' : ''}`}
              key={service.id}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {service.popular && <span className="popular-badge">Most Popular</span>}
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <ul className="service-features">
                {service.features?.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              <Link to="/contact" className="service-cta">
                Learn More →
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="process-highlight reveal">
        <h2>How We Deliver Success</h2>
        <p className="process-subtitle">Our proven methodology ensures quality, transparency, and timely delivery</p>
        <div className="process-timeline">
          <div className="timeline-item">
            <div className="timeline-dot">1</div>
            <h3>Discovery</h3>
            <p>Understanding your vision, goals, target audience, and market requirements through detailed workshops</p>
          </div>
          <div className="timeline-connector" />
          <div className="timeline-item">
            <div className="timeline-dot">2</div>
            <h3>Planning</h3>
            <p>Creating detailed roadmap, technical architecture, wireframes, and project timeline with milestones</p>
          </div>
          <div className="timeline-connector" />
          <div className="timeline-item">
            <div className="timeline-dot">3</div>
            <h3>Development</h3>
            <p>Agile development with regular sprints, updates, testing, and client feedback integration</p>
          </div>
          <div className="timeline-connector" />
          <div className="timeline-item">
            <div className="timeline-dot">4</div>
            <h3>Launch</h3>
            <p>Deployment, app store optimization, marketing support, and go-to-market strategy execution</p>
          </div>
          <div className="timeline-connector" />
          <div className="timeline-item">
            <div className="timeline-dot">5</div>
            <h3>Growth</h3>
            <p>Continuous optimization, feature updates, analytics monitoring, and scaling strategies</p>
          </div>
        </div>
      </section>

      <section className="tech-stack reveal">
        <h2>Technologies We Master</h2>
        <p className="tech-subtitle">Cutting-edge tools and frameworks to power your digital products</p>
        <div className="tech-categories">
          <div className="tech-category">
            <h3>🎮 Game Engines</h3>
            <div className="tech-tags">
              <span>Unity 3D</span>
              <span>Unreal Engine</span>
              <span>Godot</span>
              <span>Cocos2d</span>
              <span>Construct</span>
            </div>
          </div>
          <div className="tech-category">
            <h3>📱 Mobile Platforms</h3>
            <div className="tech-tags">
              <span>iOS (Swift)</span>
              <span>Android (Kotlin)</span>
              <span>React Native</span>
              <span>Flutter</span>
              <span>Xamarin</span>
            </div>
          </div>
          <div className="tech-category">
            <h3>☁️ Backend & Cloud</h3>
            <div className="tech-tags">
              <span>Firebase</span>
              <span>AWS</span>
              <span>Node.js</span>
              <span>MongoDB</span>
              <span>PostgreSQL</span>
              <span>Docker</span>
            </div>
          </div>
          <div className="tech-category">
            <h3>📈 Analytics & Tools</h3>
            <div className="tech-tags">
              <span>Google Analytics</span>
              <span>Mixpanel</span>
              <span>Adjust</span>
              <span>AppsFlyer</span>
              <span>Amplitude</span>
            </div>
          </div>
        </div>
      </section>

      <section className="industries-section reveal">
        <h2>Industries We Serve</h2>
        <p className="industries-subtitle">Deep expertise across multiple sectors and verticals</p>
        <div className="industries-grid">
          {industries.map((industry, index) => (
            <div className="industry-card" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="industry-icon">{industry.icon}</div>
              <h3>{industry.name}</h3>
              <p>{industry.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="why-us-section reveal">
        <h2>Why Choose MacroMatrix?</h2>
        <div className="why-us-grid">
          <div className="why-us-card">
            <div className="why-icon">🚀</div>
            <h3>Fast Time-to-Market</h3>
            <p>Agile methodologies and experienced teams ensure rapid development without compromising quality</p>
          </div>
          <div className="why-us-card">
            <div className="why-icon">💎</div>
            <h3>Premium Quality</h3>
            <p>Rigorous QA processes, code reviews, and testing at every stage guarantee exceptional results</p>
          </div>
          <div className="why-us-card">
            <div className="why-icon">🤝</div>
            <h3>Dedicated Support</h3>
            <p>Long-term partnership with ongoing maintenance, updates, and 24/7 technical support</p>
          </div>
          <div className="why-us-card">
            <div className="why-icon">💰</div>
            <h3>Cost-Effective</h3>
            <p>Competitive pricing with transparent budgets and no hidden costs throughout the project</p>
          </div>
        </div>
      </section>

      <section className="services-cta reveal">
        <div className="cta-content">
          <h2>Ready to Start Your Project?</h2>
          <p>Let's discuss how we can bring your vision to life with our expertise and innovative solutions. Get a free consultation today!</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get a Quote</Link>
            <Link to="/portfolio" className="btn-secondary">View Our Work</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
